import React from 'react';
import ReactDOM from 'react-dom';
import Immutable from 'immutable';
import Iso from 'iso';
import httpClient from 'superagent';
import { Provider } from 'react-redux';
import { loadableReady } from '@loadable/component';
import ConsoleErrorService from '@bibliocommons/utils-console';
import AirbrakeErrorService from '@bibliocommons/utils-airbrake';
import BCGoogleAnalyticsUtils from '@bibliocommons/utils-google-analytics';

import ApiClient from './api/ApiClient';
import AppActions from './actions/AppActions';
import ErrorActions from './actions/ErrorActions';
import { currentLibrarySelector } from './selectors/LibrarySelector';
import createReduxStore from './helpers/redux/createReduxStore';
import ApplicationContainer from './ApplicationContainer';
import ApplicationRouter from './ApplicationRouter';

/**
 * Initializes a handler for uncaught errors (browser only).
 * @param {object} errorService
 * @param {object} reduxStore
 */
function clientHandleErrors(errorService, reduxStore) {
  global.onerror = (message, fileName, lineNumber, columnNumber, error) => {
    const err = error || {
      error: { message, fileName, lineNumber, columnNumber }
    };
    errorService
      .notify(err)
      .catch(e => console.error(e)) // eslint-disable-line no-console
      .then(notice => {
        reduxStore.dispatch(ErrorActions.storeInternalError(notice));
      });
  };
}

// Prevent safari loading from cache when back button is clicked
window.onpageshow = function handleSafariBFCache(event) {
  if (event.persisted && typeof window.parentIFrame === 'undefined') {
    window.location.href += '&reload=true';
  }
};

// Boot up the React application
Iso.bootstrap(reduxStoreData => {
  const rootElement = document.getElementById('react-root');

  const {
    apiGatewayURL,
    libraryDomain,
    airbrakeConfig,
    gaTrackingId,
    enableWebpackHMR,
    enableReduxDevTools
  } = reduxStoreData.app;

  const currentLanguage = reduxStoreData.localization.currentLanguage;
  const errorService = airbrakeConfig.enabled ? new AirbrakeErrorService(airbrakeConfig) : new ConsoleErrorService();
  const apiClient = new ApiClient(httpClient, {
    subdomain: libraryDomain,
    locale: currentLanguage,
    apiGatewayURL
  });
  const reduxStore = createReduxStore(
    Immutable.fromJS(reduxStoreData),
    apiClient,
    enableWebpackHMR,
    enableReduxDevTools,
    errorService
  );

  clientHandleErrors(errorService, reduxStore);

  reduxStore.dispatch(AppActions.fetchCookies());

  // Creates and initialize GA tracking
  const currentLibrary = currentLibrarySelector(reduxStore.getState());
  BCGoogleAnalyticsUtils.initialize(gaTrackingId, currentLibrary.toJS(), currentLanguage);

  loadableReady(() => {
    ReactDOM.hydrate(
      <Provider store={reduxStore}>
        <ApplicationContainer>
          <ApplicationRouter />
        </ApplicationContainer>
      </Provider>,
      rootElement
    );
  });
});
